/**
 * @packageDocumentation
 * Offers developer tooling for forcing mock scenarios, inspecting RTP settings, and triggering automated test flows.
 */
import {
  forceNextOutcome,
  setManualRoundOverride,
  getManualRoundOverride,
  RTP_CONFIG,
  type ManualRoundOverride,
  type ManualSymbolPlan,
  type ScenarioName,
} from "./server";

const BASE_COLORS = [
  { label: "Blue", index: 0, swatch: "#3aa6ff" }, // Shows: Teal Civilian, Teal Villain
  { label: "Green", index: 1, swatch: "#3edc74" }, // Shows: Green Civilian, White Villain  
  { label: "Purple", index: 2, swatch: "#a56bff" }, // Shows: Pink Civilian, Pink Villain
  { label: "Red", index: 3, swatch: "#ff6666" }, // Shows: Orange Civilian, Orange Villain
  { label: "Yellow", index: 4, swatch: "#ffd966" }, // Shows: Rainbow Civilian, Yellow Villain
] as const;

type CharacterOption = {
  value: string;
  label: string;
  swatch: string;
};

type BorderOption = {
  value: string;
  label: string;
  swatch: string;
};

// Character mapping based on actual assets displayed
const CHARACTER_MAPPINGS = [
  { civLabel: "Teal Civilian", villainLabel: "Teal Villain", swatch: "#3aa6aa" }, // Blue assets -> Teal animations
  { civLabel: "White Civilian", villainLabel: "White Villain", swatch: "#3edc74" }, // Green civilian unchanged, Green villain -> White villain
  { civLabel: "Pink Civilian", villainLabel: "Pink Villain", swatch: "#ff6baa" }, // Purple assets -> Pink animations
  { civLabel: "Orange Civilian", villainLabel: "Orange Villain", swatch: "#ff8866" }, // Red assets -> Orange animations
  { civLabel: "Rainbow Civilian", villainLabel: "Rainbow Villain", swatch: "#ffd966" }, // Yellow civilian -> Rainbow, Yellow villain -> Rainbow
];

const CHARACTER_OPTIONS: CharacterOption[] = [
  { value: "", label: "Auto", swatch: "rgba(255,255,255,0.08)" },
  ...CHARACTER_MAPPINGS.flatMap(({ civLabel, villainLabel, swatch }, index) => [
    { value: `civ-${index}`, label: civLabel, swatch },
    { value: `sup-${index}`, label: villainLabel, swatch },
  ]),
  { value: "wild-civilian", label: "Wild Civilian", swatch: "#8f8fff" },
  { value: "wild-super", label: "Wild Villain", swatch: "#ffb347" },
];

const BORDER_OPTIONS: BorderOption[] = [
  { value: "", label: "Auto", swatch: "rgba(255,255,255,0.08)" },
  { value: "1", label: "Bronze", swatch: "#b57f43" },
  { value: "2", label: "Silver", swatch: "#c9ccd4" },
  { value: "3", label: "Gold", swatch: "#f2c94c" },
  { value: "4", label: "Holographic", swatch: "#7bd8ff" },
];

const SCENARIO_OPTIONS: { value: "" | ScenarioName; label: string }[] = [
  { value: "", label: "Automatic" },
  { value: "sameBorder", label: "Same Border" },
  { value: "sameCharacter", label: "Same Character" },
  { value: "allSuper", label: "All Super (bonus)" },
  { value: "none", label: "No Win" },
];

type SelectRow = {
  container: HTMLDivElement;
  cells: HTMLDivElement[];
  selects: HTMLSelectElement[];
};

const AUTOPLAY_HINT = "Autoplay running";

function isAutoplayRunning(): boolean {
  const manager = (globalThis as any)?.autoplay;
  return !!manager?.isRunningNow?.();
}

export class TestCasePanel {
  private root: HTMLDivElement | null = null;
  private content: HTMLDivElement | null = null;
  private toggleButton: HTMLButtonElement | null = null;
  private enableCheckbox: HTMLInputElement | null = null;
  private scenarioSelect: HTMLSelectElement | null = null;
  private manualPersist: HTMLInputElement | null = null;
  private strategySelect: HTMLSelectElement | null = null;
  private status: HTMLDivElement | null = null;

  private characterRow: SelectRow | null = null;
  private borderRow: SelectRow | null = null;

  private enabled = false;
  private visible = false;

  constructor() {
    if (typeof document === "undefined") return;
    this.build();
  }

  private build() {
    this.root = document.createElement("div");
    this.root.style.position = "fixed";
    this.root.style.top = "16px";
    this.root.style.right = "16px";
    this.root.style.zIndex = "9999";
    this.root.style.fontFamily = "Arial, sans-serif";
    this.root.style.fontSize = "13px";
    this.root.style.color = "#f5f5f5";

    this.toggleButton = document.createElement("button");
    this.toggleButton.textContent = "Test Cases";
    this.decorateButton(this.toggleButton);
    this.toggleButton.style.width = "120px";
    this.toggleButton.onclick = () => this.toggleContent();
    this.root.appendChild(this.toggleButton);

    this.content = document.createElement("div");
    this.content.style.display = "none";
    this.content.style.marginTop = "8px";
    this.content.style.padding = "14px";
    this.content.style.width = "280px";
    this.content.style.borderRadius = "12px";
    this.content.style.background = "rgba(73, 76, 191, 0.95)";
    this.content.style.boxShadow = "0 8px 22px rgba(0,0,0,0.4)";
    this.content.style.backdropFilter = "blur(4px)";
    this.root.appendChild(this.content);

    const enableRow = document.createElement("label");
    enableRow.style.display = "flex";
    enableRow.style.alignItems = "center";
    enableRow.style.gap = "6px";

    this.enableCheckbox = document.createElement("input");
    this.enableCheckbox.type = "checkbox";
    this.enableCheckbox.onchange = () => this.onEnableChanged();
    enableRow.appendChild(this.enableCheckbox);

    const enableLabel = document.createElement("span");
    enableLabel.textContent = "Enable test mode";
    enableRow.appendChild(enableLabel);
    this.content.appendChild(enableRow);

    this.content.appendChild(this.makeDivider("Scenario"));

    this.scenarioSelect = document.createElement("select");
    this.decorateSelect(this.scenarioSelect);
    this.scenarioSelect.style.width = "100%";
    SCENARIO_OPTIONS.forEach((opt) => {
      const option = document.createElement("option");
      option.value = opt.value;
      option.textContent = opt.label;
      this.scenarioSelect?.appendChild(option);
    });
    this.content.appendChild(this.scenarioSelect);

    const scenarioActions = document.createElement("div");
    scenarioActions.style.display = "flex";
    scenarioActions.style.gap = "8px";
    scenarioActions.style.marginTop = "6px";

    const applyScenarioBtn = document.createElement("button");
    applyScenarioBtn.textContent = "Apply";
    this.decorateButton(applyScenarioBtn);
    applyScenarioBtn.style.flex = "1";
    applyScenarioBtn.onclick = () => this.applyScenario();
    scenarioActions.appendChild(applyScenarioBtn);

    const clearScenarioBtn = document.createElement("button");
    clearScenarioBtn.textContent = "Clear";
    this.decorateButton(clearScenarioBtn);
    clearScenarioBtn.style.flex = "1";
    clearScenarioBtn.onclick = () => this.clearScenario();
    scenarioActions.appendChild(clearScenarioBtn);

    this.content.appendChild(scenarioActions);

    this.content.appendChild(this.makeDivider("Manual cards"));

    this.characterRow = this.createSelectRow("Character", CHARACTER_OPTIONS);
    this.content.appendChild(this.characterRow.container);

    this.borderRow = this.createSelectRow("Border", BORDER_OPTIONS);
    this.borderRow.container.style.marginTop = "8px";
    this.content.appendChild(this.borderRow.container);

    const strategyRow = document.createElement("div");
    strategyRow.style.display = "flex";
    strategyRow.style.justifyContent = "space-between";
    strategyRow.style.alignItems = "center";
    strategyRow.style.marginTop = "8px";

    const strategyLabel = document.createElement("span");
    strategyLabel.textContent = "Transform preference";
    strategyLabel.style.fontSize = "12px";
    strategyLabel.style.opacity = "0.8";
    strategyRow.appendChild(strategyLabel);

    this.strategySelect = document.createElement("select");
    this.decorateSelect(this.strategySelect);
    this.strategySelect.style.flex = "1";
    this.strategySelect.style.marginLeft = "8px";
    [
      { value: "bestPayout", label: "Best" },
      { value: "preferSameBorder", label: "Border" },
      { value: "preferSameCharacter", label: "Character" },
    ].forEach((opt) => {
      const option = document.createElement("option");
      option.value = opt.value;
      option.textContent = opt.label;
      this.strategySelect?.appendChild(option);
    });
    this.strategySelect.value = RTP_CONFIG.transform.strategy;
    strategyRow.appendChild(this.strategySelect);
    this.content.appendChild(strategyRow);

    const persistRow = document.createElement("label");
    persistRow.style.display = "flex";
    persistRow.style.alignItems = "center";
    persistRow.style.gap = "6px";
    persistRow.style.marginTop = "6px";

    this.manualPersist = document.createElement("input");
    this.manualPersist.type = "checkbox";
    persistRow.appendChild(this.manualPersist);

    const persistText = document.createElement("span");
    persistText.textContent = "Persist across spins";
    persistRow.appendChild(persistText);
    this.content.appendChild(persistRow);

    const manualActions = document.createElement("div");
    manualActions.style.display = "flex";
    manualActions.style.gap = "8px";
    manualActions.style.marginTop = "8px";

    const applyManualBtn = document.createElement("button");
    applyManualBtn.textContent = "Apply manual";
    this.decorateButton(applyManualBtn);
    applyManualBtn.style.flex = "1";
    applyManualBtn.onclick = () => this.applyManual();
    manualActions.appendChild(applyManualBtn);

    const disableManualBtn = document.createElement("button");
    disableManualBtn.textContent = "Disable";
    this.decorateButton(disableManualBtn);
    disableManualBtn.style.flex = "1";
    disableManualBtn.onclick = () => this.clearOverrides();
    manualActions.appendChild(disableManualBtn);

    this.content.appendChild(manualActions);

    this.status = document.createElement("div");
    this.status.style.marginTop = "10px";
    this.status.style.minHeight = "18px";
    this.status.style.fontSize = "12px";
    this.status.style.color = "#8fd3ff";
    this.content.appendChild(this.status);

    this.createOptionHandlers();

    document.body.appendChild(this.root);
    this.updateEnabledState();
  }

  private createSelectRow(labelText: string, options: CharacterOption[] | BorderOption[]): SelectRow {
    const wrap = document.createElement("div");
    wrap.style.display = "flex";
    wrap.style.flexDirection = "column";
    wrap.style.gap = "6px";

    const label = document.createElement("span");
    label.textContent = labelText;
    label.style.fontSize = "12px";
    label.style.opacity = "0.8";
    wrap.appendChild(label);

    const grid = document.createElement("div");
    grid.style.display = "grid";
    grid.style.gridTemplateColumns = "repeat(3, 1fr)";
    grid.style.gap = "6px";
    wrap.appendChild(grid);

    const cells: HTMLDivElement[] = [];
    const selects: HTMLSelectElement[] = [];

    for (let i = 0; i < 3; i++) {
      const cell = document.createElement("div");
      cell.style.borderRadius = "8px";
      cell.style.background = "rgba(255,255,255,0.08)";
      cell.style.padding = "4px";
      cell.style.display = "flex";
      cell.style.justifyContent = "center";
      cell.style.alignItems = "center";

      const select = document.createElement("select");
      this.decorateSelect(select);
      select.style.width = "100%";
      select.style.background = "rgba(0,0,0,0.25)";
      select.style.border = "1px solid rgba(255,255,255,0.12)";
      select.style.textAlign = "center";
      select.style.fontWeight = "600";

      options.forEach((opt) => {
        const option = document.createElement("option");
        option.value = (opt as any).value;
        option.textContent = (opt as any).label;
        select.appendChild(option);
      });

      cell.appendChild(select);
      grid.appendChild(cell);

      cells.push(cell);
      selects.push(select);
    }

    return { container: wrap, cells, selects };
  }

  private decorateSelect(select: HTMLSelectElement) {
    select.style.padding = "6px";
    select.style.borderRadius = "6px";
    select.style.border = "1px solid rgba(255,255,255,0.15)";
    select.style.background = "rgba(10,10,18,0.8)";
    select.style.color = "#f5f5f5";
    select.style.fontSize = "12px";
    select.style.cursor = "pointer";
  }

  private decorateButton(button: HTMLButtonElement) {
    button.style.padding = "6px 12px";
    button.style.border = "none";
    button.style.borderRadius = "6px";
    button.style.background = "#040c6365";
    button.style.color = "#fff";
    button.style.cursor = "pointer";
    button.style.fontWeight = "600";
    button.style.transition = "background 0.2s";
    button.onmouseenter = () => (button.style.background = "#040c63de");
    button.onmouseleave = () => (button.style.background = "#040c6365");
  }

  private makeDivider(label: string) {
    const wrap = document.createElement("div");
    wrap.style.display = "flex";
    wrap.style.alignItems = "center";
    wrap.style.gap = "6px";
    wrap.style.margin = "10px 0 6px";

    const title = document.createElement("span");
    title.textContent = label;
    title.style.fontWeight = "bold";
    title.style.fontSize = "12px";
    wrap.appendChild(title);

    const rule = document.createElement("div");
    rule.style.flex = "1";
    rule.style.height = "1px";
    rule.style.background = "rgba(255,255,255,0.18)";
    wrap.appendChild(rule);

    return wrap;
  }

  private onEnableChanged() {
    this.enabled = !!this.enableCheckbox?.checked;
    this.updateEnabledState();
    if (!this.enabled) {
      this.clearOverrides();
      this.showStatus("Test mode disabled");
    } else if (this.visible) {
      this.hydrateFromState();
    }
  }

  private updateEnabledState() {
    const disabled = !this.enabled;
    if (!this.content) return;
    const interactive = this.content.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLButtonElement>("input, select, button");
    interactive.forEach((el) => {
      if (el === this.enableCheckbox) return;
      el.disabled = disabled;
      if (disabled) {
        el.style.cursor = "not-allowed";
      } else {
        el.style.cursor = "pointer";
      }
    });
    this.content.style.opacity = disabled ? "0.75" : "1";
    if (this.characterRow && this.borderRow) {
      for (let i = 0; i < 3; i++) {
        this.syncBorderLock(i);
      }
    }
  }

  private toggleContent() {
    if (!this.content) return;
    this.visible = !this.visible;
    this.content.style.display = this.visible ? "block" : "none";
    if (this.visible && this.enabled) {
      this.hydrateFromState();
    }
  }

  private hydrateFromState() {
    const current = getManualRoundOverride();
    if (this.manualPersist) {
      this.manualPersist.checked = !!current?.persist;
    }
    if (this.strategySelect && current?.transformStrategy) {
      this.strategySelect.value = current.transformStrategy;
    }

    for (let i = 0; i < 3; i++) {
      const plan = current?.cards?.[i];
      const charSelect = this.characterRow?.selects[i];
      const borderSelect = this.borderRow?.selects[i];
      if (!charSelect || !borderSelect) continue;

      if (!plan) {
        charSelect.value = "";
        borderSelect.value = "";
      } else if (plan.type === "wildCivilian") {
        charSelect.value = "wild-civilian";
        borderSelect.value = "";
      } else if (plan.type === "wildSuper") {
        charSelect.value = "wild-super";
        borderSelect.value = "";
      } else if (plan.type === "civilian") {
        charSelect.value = `civ-${plan.color ?? 0}`;
        borderSelect.value = plan.border ? String(plan.border) : "";
      } else if (plan.type === "super") {
        charSelect.value = `sup-${plan.color ?? 0}`;
        borderSelect.value = plan.border ? String(plan.border) : "";
      } else {
        charSelect.value = "";
        borderSelect.value = "";
      }

      this.applyCharacterStyling(i);
      this.applyBorderStyling(i);
      this.syncBorderLock(i);
    }
  }

  private applyCharacterStyling(index: number) {
    if (!this.characterRow) return;
    const select = this.characterRow.selects[index];
    const cell = this.characterRow.cells[index];
    const option = CHARACTER_OPTIONS.find((opt) => opt.value === select.value);
    cell.style.background = option?.swatch ?? "rgba(255,255,255,0.08)";
    cell.style.color = select.value ? "#141414" : "#141414";
    select.style.color = select.value ? "#141414" : "#141414";
    select.style.background = option ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.25)";
  }

  private applyBorderStyling(index: number) {
    if (!this.borderRow) return;
    const select = this.borderRow.selects[index];
    const cell = this.borderRow.cells[index];
    const option = BORDER_OPTIONS.find((opt) => opt.value === select.value);
    cell.style.background = option?.swatch ?? "rgba(255,255,255,0.08)";
    cell.style.color = select.value ? "#141414" : "#141414";
    select.style.color = select.value ? "#141414" : "#141414";
    select.style.background = option ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.25)";
  }

  private syncBorderLock(index: number) {
    if (!this.characterRow || !this.borderRow) return;
    const charValue = this.characterRow.selects[index].value;
    const borderSelect = this.borderRow.selects[index];
    const borderCell = this.borderRow.cells[index];
    const isWild = charValue.startsWith("wild-");
    borderSelect.disabled = isWild || !this.enabled;
    borderCell.style.opacity = borderSelect.disabled ? "0.55" : "1";
    if (isWild) {
      borderSelect.value = "";
      this.applyBorderStyling(index);
    }
  }

  private onCharacterChange(index: number) {
    this.applyCharacterStyling(index);
    this.syncBorderLock(index);
  }

  private onBorderChange(index: number) {
    this.applyBorderStyling(index);
  }

  private applyScenario() {
    if (!this.enabled || !this.scenarioSelect) {
      this.showStatus("Enable test mode first");
      return;
    }
    const value = this.scenarioSelect.value as "" | ScenarioName;
    if (!value) {
      forceNextOutcome(null);
      this.showStatus("Scenario auto reset");
      return;
    }
    forceNextOutcome(value);
    setManualRoundOverride(null);
    this.showStatus(`Scenario forced to ${value}`);
  }

  private clearScenario() {
    forceNextOutcome(null);
    this.showStatus("Scenario cleared");
  }

  private applyManual() {
    if (!this.enabled || !this.characterRow || !this.borderRow) {
      this.showStatus("Enable test mode first");
      return;
    }

    const cards = this.characterRow.selects.map((select, index) => {
      const value = select.value;
      const borderValue = this.borderRow!.selects[index].value;
      const plan: ManualSymbolPlan = {};

      if (value.startsWith("wild-")) {
        plan.type = value === "wild-super" ? "wildSuper" : "wildCivilian";
      } else if (value.startsWith("civ-")) {
        plan.type = "civilian";
        plan.color = Number(value.split("-")[1]);
      } else if (value.startsWith("sup-")) {
        plan.type = "super";
        plan.color = Number(value.split("-")[1]);
      }

      if (!value.startsWith("wild-") && borderValue) {
        plan.border = Number(borderValue) as 1 | 2 | 3 | 4;
      }

      return plan;
    }) as [ManualSymbolPlan, ManualSymbolPlan, ManualSymbolPlan];

    const hasDirective = cards.some((card) => Object.keys(card).length > 0);
    if (!hasDirective) {
      setManualRoundOverride(null);
      forceNextOutcome(null);
      this.showStatus("Manual override cleared");
      return;
    }

    const override: ManualRoundOverride = {
      cards: cards,
      transformStrategy: (this.strategySelect?.value as ManualRoundOverride["transformStrategy"]) ?? RTP_CONFIG.transform.strategy,
      persist: !!this.manualPersist?.checked,
    };

    setManualRoundOverride(override);
    forceNextOutcome(null);

    if (cards.some((card) => card.type === "wildCivilian" || card.type === "wildSuper")) {
      this.showStatus("Wild cards lock to the highest win");
    } else {
      this.showStatus(override.persist ? "Manual override latched" : "Manual override queued");
    }
  }

  private clearOverrides() {
    setManualRoundOverride(null);
    forceNextOutcome(null);
    if (this.characterRow && this.borderRow) {
      for (let i = 0; i < 3; i++) {
        this.characterRow.selects[i].value = "";
        this.borderRow.selects[i].value = "";
        this.applyCharacterStyling(i);
        this.applyBorderStyling(i);
        this.syncBorderLock(i);
      }
    }
    this.showStatus("Overrides disabled");
  }

  private showStatus(message: string) {
    if (!this.status) return;
    this.status.textContent = isAutoplayRunning() ? `${message} (${AUTOPLAY_HINT})` : message;
  }

  private createOptionHandlers() {
    if (!this.characterRow || !this.borderRow) return;
    for (let i = 0; i < 3; i++) {
      this.characterRow.selects[i].addEventListener("change", () => this.onCharacterChange(i));
      this.borderRow.selects[i].addEventListener("change", () => this.onBorderChange(i));
      this.applyCharacterStyling(i);
      this.applyBorderStyling(i);
      this.syncBorderLock(i);
    }
  }
}
